import {
  ChevronLeft,
  FileImage,
  Gauge,
  Home,
  LogOut,
  Mail,
  Menu,
  MonitorCog,
  PanelsTopLeft,
  Quote,
  Users,
  X,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { usePermissions } from '../../hooks/usePermissions.js';
import { brand } from '../../data/siteData.js';
import { api, unwrap } from '../../services/api.js';

const RAIL_KEY = 'beam-admin-rail';

const groups = [
  {
    label: 'admin.navOverview',
    items: [['/admin', 'admin.dashboard', Gauge, () => true]],
  },
  {
    label: 'admin.navContent',
    items: [
      ['/admin/homepage', 'admin.homepage', Home, (can) => can.canEditContent],
      ['/admin/services', 'admin.services', MonitorCog, (can) => can.canEditContent],
      ['/admin/projects', 'admin.projects', PanelsTopLeft, (can) => can.canEditContent],
      ['/admin/testimonials', 'admin.testimonials', Quote, (can) => can.canEditContent],
      ['/admin/media', 'admin.media', FileImage, (can) => can.canManageMedia],
    ],
  },
  {
    label: 'admin.navOperations',
    items: [
      ['/admin/contacts', 'admin.contacts', Mail, (can) => can.canViewContacts],
      ['/admin/users', 'admin.users', Users, (can) => can.canManageUsers],
    ],
  },
];

const roleTone = {
  'super-admin': 'bg-beam-blue text-white',
  admin: 'bg-beam-sky text-white',
  editor: 'bg-beam-light text-beam-graphite',
};

export const AdminLayout = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const permissions = usePermissions();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [railCollapsed, setRailCollapsed] = useState(() => localStorage.getItem(RAIL_KEY) === '1');
  const drawerRef = useRef(null);
  const toggleRef = useRef(null);

  const { data: analytics } = useQuery({
    queryKey: ['analytics'],
    queryFn: () => api.get('/analytics').then(unwrap),
    // /analytics is adminOnly on the server; an editor polling it every 30s
    // produced a 403 on every admin page load.
    enabled: permissions.canViewAnalytics,
    refetchInterval: () => (document.visibilityState === 'visible' ? 30_000 : false),
  });

  const unreadContacts = analytics?.newContacts || 0;

  const visibleGroups = useMemo(
    () =>
      groups
        .map((group) => ({
          ...group,
          items: group.items.filter(([, , , allowed]) => allowed(permissions)),
        }))
        .filter((group) => group.items.length),
    [permissions],
  );

  const currentLabel = useMemo(() => {
    const all = visibleGroups.flatMap((group) => group.items);
    const match = all
      .filter(([to]) => (to === '/admin' ? location.pathname === '/admin' : location.pathname.startsWith(to)))
      .sort((a, b) => b[0].length - a[0].length)[0];
    return match ? t(match[1]) : t('admin.dashboard');
  }, [location.pathname, visibleGroups, t]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem(RAIL_KEY, railCollapsed ? '1' : '0');
  }, [railCollapsed]);

  useEffect(() => {
    if (!drawerOpen) return undefined;

    document.body.classList.add('admin-scroll-lock');
    const frame = window.requestAnimationFrame(() => {
      drawerRef.current?.querySelector('a, button')?.focus();
    });

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setDrawerOpen(false);
        toggleRef.current?.focus();
        return;
      }
      if (event.key !== 'Tab') return;

      const nodes = Array.from(drawerRef.current?.querySelectorAll('a[href], button:not([disabled])') || []);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('admin-scroll-lock');
      window.cancelAnimationFrame(frame);
    };
  }, [drawerOpen]);

  const renderNav = (compact) => (
    <nav className="grid gap-5 p-3" aria-label={t('admin.primaryNavigation')}>
      {visibleGroups.map((group) => (
        <div key={group.label} className="grid gap-1">
          {compact ? null : (
            <p className="px-3 pb-1 text-[10px] font-extrabold uppercase tracking-[0.18em] text-beam-graphite/45">
              {t(group.label)}
            </p>
          )}
          {group.items.map(([to, label, Icon]) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/admin'}
              title={compact ? t(label) : undefined}
              className={({ isActive }) =>
                `focus-ring relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-bold transition ${
                  compact ? 'justify-center' : ''
                } ${isActive ? 'bg-beam-blue text-white' : 'text-beam-graphite hover:bg-beam-light'}`
              }
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              {compact ? null : <span className="min-w-0 flex-1 truncate">{t(label)}</span>}
              {to === '/admin/contacts' && unreadContacts > 0 ? (
                <span
                  className={`rounded-md bg-state-critical px-1.5 py-0.5 text-[11px] font-extrabold text-white ${
                    compact ? 'absolute right-1 top-1' : ''
                  }`}
                >
                  {unreadContacts}
                  <span className="sr-only"> {t('admin.unread')}</span>
                </span>
              ) : null}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#eef1f8] text-beam-ink">
      <a
        href="#admin-main"
        className="focus-ring sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[80] focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-bold"
      >
        {t('admin.skipToContent')}
      </a>

      <div className={`lg:grid ${railCollapsed ? 'lg:grid-cols-[84px_1fr]' : 'lg:grid-cols-[272px_1fr]'}`}>
        <aside className="sticky top-0 hidden h-screen overflow-y-auto border-r border-beam-blue/10 bg-white lg:block">
          <div
            className={`flex h-20 items-center gap-2 border-b border-beam-blue/10 px-4 ${
              railCollapsed ? 'justify-center' : 'justify-between'
            }`}
          >
            {railCollapsed ? null : <img src={brand.logo} alt="BEAM" className="h-8 w-auto" />}
            <span className="rounded-lg bg-beam-light px-2 py-1 text-xs font-extrabold text-beam-blue">CMS</span>
          </div>
          {renderNav(railCollapsed)}
          <div className="p-3">
            <button
              type="button"
              onClick={() => setRailCollapsed((value) => !value)}
              className="focus-ring flex w-full items-center justify-center gap-2 rounded-lg border border-beam-blue/10 px-3 py-2 text-xs font-extrabold text-beam-graphite hover:bg-beam-light"
              aria-label={railCollapsed ? t('admin.expandMenu') : t('admin.collapseMenu')}
            >
              <ChevronLeft className={`h-4 w-4 transition ${railCollapsed ? 'rotate-180' : ''}`} aria-hidden="true" />
              {railCollapsed ? null : t('admin.collapseMenu')}
            </button>
          </div>
        </aside>

        {drawerOpen ? (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div
              className="absolute inset-0 bg-beam-ink/60 backdrop-blur-sm"
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
            />
            <div
              ref={drawerRef}
              role="dialog"
              aria-modal="true"
              aria-label={t('admin.primaryNavigation')}
              className="absolute inset-y-0 left-0 flex w-[min(300px,85vw)] flex-col overflow-y-auto bg-white shadow-crisp"
            >
              <div className="flex h-20 items-center justify-between border-b border-beam-blue/10 px-4">
                <img src={brand.logo} alt="BEAM" className="h-8 w-auto" />
                <button
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  className="focus-ring grid h-10 w-10 place-items-center rounded-lg text-beam-graphite hover:bg-beam-light"
                  aria-label={t('common.close')}
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              {renderNav(false)}
            </div>
          </div>
        ) : null}

        <div className="min-w-0">
          <header className="sticky top-0 z-30 border-b border-beam-blue/10 bg-white/95 backdrop-blur">
            <div className="flex h-20 items-center justify-between gap-3 px-4 sm:px-6">
              <div className="flex min-w-0 items-center gap-3">
                <button
                  ref={toggleRef}
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  className="focus-ring grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-beam-blue/10 text-beam-ink lg:hidden"
                  aria-label={t('admin.openMenu')}
                  aria-expanded={drawerOpen}
                >
                  <Menu className="h-5 w-5" aria-hidden="true" />
                </button>
                <div className="min-w-0">
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-beam-blue">BEAM CMS</p>
                  <p className="truncate font-display text-lg font-extrabold leading-tight">{currentLabel}</p>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <div className="hidden rounded-lg bg-beam-light p-1 sm:flex">
                  {['fr', 'en'].map((language) => (
                    <button
                      key={language}
                      type="button"
                      onClick={() => i18n.changeLanguage(language)}
                      aria-pressed={i18n.language === language}
                      className={`focus-ring rounded-md px-2.5 py-1 text-xs font-extrabold uppercase transition ${
                        i18n.language === language ? 'bg-beam-ink text-white' : 'text-beam-graphite hover:bg-white'
                      }`}
                    >
                      {language}
                    </button>
                  ))}
                </div>

                <div className="hidden text-right md:block">
                  <p className="truncate text-sm font-bold leading-tight">{user?.name}</p>
                  <span
                    className={`mt-0.5 inline-block rounded-md px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide ${
                      roleTone[permissions.role] || roleTone.editor
                    }`}
                  >
                    {t(`admin.role_${permissions.role}`, { defaultValue: permissions.role })}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={logout}
                  className="focus-ring inline-flex h-11 items-center gap-2 rounded-lg border border-beam-blue/10 bg-white px-3 text-sm font-bold text-beam-ink hover:bg-beam-light"
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden sm:inline">{t('common.logout')}</span>
                </button>
              </div>
            </div>
          </header>

          <main id="admin-main" tabIndex={-1} className="p-4 outline-none sm:p-6 lg:p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};
